import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { FieldsetModule } from 'primeng/fieldset';
import { ImageModule } from 'primeng/image';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { WebcamModule, WebcamImage } from 'ngx-webcam';
import { Subject } from 'rxjs';
import { SchedaInterventoService } from '@/core/service/scheda-intervento.service';
import { finalize } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
    selector: 'app-indagine-interventi',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FieldsetModule, ButtonModule, InputTextModule, ImageModule, DividerModule, ProgressSpinnerModule, WebcamModule, DialogModule, ConfirmDialogModule],
    templateUrl: './indagine-interventi.component.html',
    providers: [ConfirmationService]
})
export class IndagineInterventiComponent {
    @Input() form!: FormGroup;
    @Input() deviatoioId: any;
    @Input() dataRilevamento: any;

    showWebcam = false;
    previewImage: string | null = null;
    isUploading = false;
    isLoadingImage: boolean = false;
    currentIndex: number | null = null;
    interventoId: number | null = null;
    trigger: Subject<void> = new Subject<void>();

    constructor(
        private confirmationService: ConfirmationService,
        private schedaInterventoService: SchedaInterventoService,
        private deviatoioSchedaInterventiService: SchedaInterventoService,
        private messageService: MessageService
    ) {}

    get interventi(): FormArray {
        return this.form.get('interventi') as FormArray;
    }

    get triggerObservable() {
        return this.trigger.asObservable();
    }

    ngOnInit(): void {
        const data = this.form.get('interventi')?.value || [];
        // const data = this.form.value.interventi || [];
        if (!data.length) {
            return;
        }

        const interventiArray = this.interventi;
        interventiArray.clear();

        data.forEach((interv: any) => {
            interventiArray.push(
                new FormGroup({
                    id: new FormControl(interv.id || null),
                    noteInterventoRilevatore: new FormControl(interv?.noteInterventoRilevatore),
                    pathFile: new FormControl(interv?.pathFile),
                    foto: new FormControl(interv.foto || null),
                    existFile: new FormControl(interv?.existFile)
                })
            );
        });
    }

    addIntervento() {
        this.interventi.push(
            new FormGroup({
                noteInterventoRilevatore: new FormControl(),
                pathFile: new FormControl(),
                foto: new FormControl(null),
                existFile: new FormControl(false)
            })
        );
    }

    removeIntervento(index: number, interv: any): void {
        this.confirmationService.confirm({
            message: 'Sei sicuro di voler eliminare questo intervento?',
            header: 'Conferma eliminazione',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sì',
            rejectLabel: 'No',
            acceptButtonStyleClass: 'p-button-danger p-button-sm',
            rejectButtonStyleClass: 'p-button-secondary p-button-sm',
            accept: () => {
                this.interventi.removeAt(index);

                // Usa il getter
                const dataRilevamento = this.dataRilevamentoValue;

                const uploadFoto = {
                    deviatoioId: this.deviatoioId,
                    interventoId: interv.value.id,
                    dateFolder: dataRilevamento,
                    fileName: null
                };

                this.deviatoioSchedaInterventiService.delete(uploadFoto).subscribe({});
            }
        });
    }

    openCamera(index: number, interv: any): void {
        this.showWebcam = true;
        this.previewImage = null;
        this.currentIndex = index;
        this.interventoId = interv.value.id ?? null;
    }

    captureImage(): void {
        this.trigger.next();
    }

    handleImage(webcamImage: WebcamImage): void {
        if (this.currentIndex === null) return;

        const base64 = webcamImage.imageAsDataUrl;
        const file = this.base64ToFile(base64, `intervento_${this.currentIndex + 1}.png`);

        const current = this.interventi.at(this.currentIndex);

        // Usa il getter invece di this.dataRilevamento
        const dataRilevamento = this.dataRilevamentoValue;

        current.patchValue({
            foto: base64,
            pathFile: file.name,
            dateFolder: dataRilevamento,
            existFile: true
        });

        const formData = new FormData();
        const dto = {
            deviatoioId: this.deviatoioId,
            dateFolder: dataRilevamento, // Usa la variabile locale
            interventoId: this.interventoId ?? null,
            fileName: file.name
        };

        formData.append('files', file);
        // Il backend riceve 'data' come @RequestPart String e la parsa a mano: stringa semplice.
        formData.append('data', JSON.stringify(dto));

        this.isUploading = true;
        this.schedaInterventoService
            .uploadOD(formData)
            .pipe(finalize(() => (this.isUploading = false)))
            .subscribe({
                next: () => {
                    this.showWebcam = false;
                    current.patchValue({ pathFile: file.name });
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Foto caricata',
                        detail: `File ${file.name} salvato correttamente`
                    });
                },
                error: (err) => {
                    console.error('Errore upload:', err);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Errore upload',
                        detail: 'Impossibile caricare la foto'
                    });
                }
            });
    }

    openInterventoImage(i: number): void {
        this.showWebcam = false;
        this.isLoadingImage = true;
        const interventoId = this.interventi.at(i).value.id;

        if (!interventoId) {
            this.messageService.add({ severity: 'warn', summary: 'Nessuna foto', detail: 'Intervento non salvato' });
            return;
        }

        // Usa il getter
        const dataRilevamento = this.dataRilevamentoValue;

        const dto = {
            deviatoioId: this.deviatoioId,
            dateFolder: dataRilevamento,
            interventoId: interventoId
        };

        this.deviatoioSchedaInterventiService
            .getFilePreview(dto)
            .pipe(finalize(() => (this.isLoadingImage = false)))
            .subscribe({
                next: (blob) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        this.previewImage = reader.result as string;
                        this.showWebcam = true;
                    };
                    reader.readAsDataURL(blob);
                },
                error: (err) => {
                    console.error('Errore recupero foto:', err);
                    this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile caricare la foto' });
                }
            });
    }

    private base64ToFile(base64: string, filename: string): File {
        const [meta, content] = base64.split(',');
        const mime = meta.match(/:(.*?);/)![1];
        const binary = atob(content);
        const array = Uint8Array.from(binary, (c) => c.charCodeAt(0));
        return new File([array], filename, { type: mime });
    }

    get dataRilevamentoValue(): string | null {
        const date = this.form.get('dataRilevamento')?.value;
        if (!date) return null;

        try {
            const d = new Date(date);
            // toISOString restituisce formato ISO (es: 2025-10-20T11:24:37.000Z)
            // split('T')[0] prende solo la parte della data
            return d.toISOString().split('T')[0];
        } catch (e) {
            console.error('Errore formattazione data:', e);
            return null;
        }
    }
}
